import { useCallback, useContext } from 'react';
import { useLocation } from '@tanstack/react-router';
import { Text, Node, Path, Range, Element } from 'slate';
import { findCitationWithContext } from '../utils';
import { useComments } from 'entities/comment';

import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { CustomEditor } from 'slate/types/slate';

interface DecorateCommentsOptions {
  sectionSuperId: string;
}

type DecorationRange = Range & { commentId: string; activeComment: boolean };

export const withDecorateComments = (options: DecorateCommentsOptions) => (editor: CustomEditor) => {
  const { sectionSuperId } = options;
  const { comments } = useComments(sectionSuperId);
  const sidebarContext = useContext(SidebarContext);
  const { pathname } = useLocation();
  return useCallback(
    ([node, path]: [Node, Path]) => {
      const ranges: DecorationRange[] = [];
      if (pathname.includes('pdf')) {
        return ranges;
      }
      if (!Text.isText(node)) {
        return ranges;
      }
      const parentPath = path.slice(0, -1);
      let parentNode: Node;
      try {
        parentNode = Node.get(editor, parentPath);
      } catch {
        return ranges;
      }
      const parentParagraphId = Element.isElement(parentNode) ? parentNode.uuid ?? null : null;

      const paragraphComments = comments?.filter((comment) => comment.onId === parentParagraphId && !comment.closedAt) ?? [];
      if (paragraphComments.length === 0) {
        return ranges;
      }

      const paragraphText = Node.string(parentNode);
      const relativePath = path.slice(parentPath.length);
      let nodeStart = 0;
      for (const [textNode, textPath] of Node.texts(parentNode)) {
        if (Path.equals(textPath, relativePath)) {
          break;
        }
        nodeStart += textNode.text.length;
      }
      const nodeEnd = nodeStart + node.text.length;

      paragraphComments.forEach((comment) => {
        const activeComment = comment.id === sidebarContext?.activeComment?.id;
        const occurences = findCitationWithContext(paragraphText, comment.citation);

        if (occurences.length === 0) {
          ranges.push({
            anchor: { path, offset: 0 },
            focus: { path, offset: node.text.length },
            commentId: comment.id,
            activeComment,
          });
          return;
        }

        occurences.forEach((occurence) => {
          const start = Math.max(occurence.start, nodeStart);
          const end = Math.min(occurence.end, nodeEnd);
          if (start >= end) {
            return;
          }
          ranges.push({
            anchor: { path, offset: start - nodeStart },
            focus: { path, offset: end - nodeStart },
            commentId: comment.id,
            activeComment,
          });
        });
      });

      return ranges;
    },
    [comments, editor, pathname, sidebarContext]
  );
};
